// Provider stack per §3 of the frontend proposal:
// ErrorBoundary > BrowserRouter > Auth0ProviderWithNavigate > QueryClientProvider > AppShell routes.
// BrowserRouter sits above Auth0ProviderWithNavigate because the latter calls
// useNavigate() in its onRedirectCallback. The boundary sits outside both on
// purpose (see ErrorBoundary.tsx).
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ErrorBoundary } from './states/ErrorBoundary';
import { Auth0ProviderWithNavigate } from '../auth/Auth0ProviderWithNavigate';
import { MoneyFormatProvider } from '../lib/money';
// Radix tooltips need a provider at the root. MerchantName (and any other
// tooltip consumer) throws without one, so it is mounted globally here rather
// than per screen.
import { TooltipProvider } from '@moneyskyline/ui';
import { RouteHeadingFocus } from './navigation/RouteHeadingFocus';
import { JobRegistryProvider } from '../features/transactions/tools/JobRegistryProvider';
import routeManifest from './routes/routeManifest';
import { renderRouteManifest } from './routes/renderRouteManifest';
import { routeBindings, routeGuards } from './routes/routeBindings';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function AppRoutes() {
  return (
    <>
      <RouteHeadingFocus />
      <Routes>{renderRouteManifest(routeManifest, routeBindings, routeGuards)}</Routes>
    </>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      {/* Successive filter actions must observe the committed URL in BrowserRouter. */}
      <BrowserRouter useTransitions={false}>
        <Auth0ProviderWithNavigate>
          <QueryClientProvider client={queryClient}>
            <MoneyFormatProvider>
              <TooltipProvider delayDuration={200}>
                <JobRegistryProvider>
                  <AppRoutes />
                  <Toaster />
                </JobRegistryProvider>
              </TooltipProvider>
            </MoneyFormatProvider>
          </QueryClientProvider>
        </Auth0ProviderWithNavigate>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
